import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API } from '../config/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { typeLabel } from '../config/horseOptions';


// Render body text with **bold** support
const renderBody = (text) => {
  if (!text) return null;
  return text.split('\n\n').map((paragraph, pIdx) => {
    // Split by **bold** markers
    const parts = paragraph.split(/(\*\*[^*]+\*\*)/g);
    return (
      <p key={pIdx} className="text-[#0a1628] text-base leading-relaxed mb-6">
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} className="font-bold">{part.slice(2, -2)}</strong>;
          }
          return <span key={i}>{part}</span>;
        })}
      </p>
    );
  });
};

const ResultDetailPage = () => {
  const [results, setResults] = useState([]);
  const [article, setArticle] = useState(null);
  const [attualita, setAttualita] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const dateKey = location.pathname.split('/').pop();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resResponse, attualitaResponse, articleResponse] = await Promise.all([
          axios.get(`${API}/results`),
          axios.get(`${API}/attualita?limit=4`).catch(() => ({ data: [] })),
          axios.get(`${API}/result-articles/${dateKey}`).catch(() => ({ data: null }))
        ]);
        const filtered = resResponse.data.filter(r => {
          const rDate = new Date(r.end_time).toISOString().split('T')[0];
          return rDate === dateKey;
        });
        setResults(filtered);
        setAttualita(attualitaResponse.data || []);
        setArticle(articleResponse.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [dateKey]);

  const handleShare = (platform) => {
    const url = window.location.href;
    const text = `Risultati dell'asta - ${dateLabel}`;
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        break;
      default:
        break;
    }
  };

  if (loading) return <LoadingSpinner />;
  if (results.length === 0) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-[#64748b] text-lg mb-4">Nessun risultato per questa data</p>
        <a href="/risultati" className="text-[#0a7e8c] font-medium hover:underline">Torna ai risultati</a>
      </div>
    </div>
  );

  const dateLabel = new Date(dateKey + 'T12:00:00').toLocaleDateString('it-IT', {
    day: 'numeric', month: 'long', year: 'numeric'
  });
  const topHorse = results.reduce((best, a) => (!best || a.current_price > best.current_price) ? a : best, null);
  const heroImage = article?.image_url || topHorse?.horse?.images?.[0];

  return (
    <div className="bg-white min-h-screen" data-testid="result-detail-page">
      {/* Hero section - dark navy */}
      <section className="bg-[#0a1628]" data-testid="result-detail-hero">
        <div className="container-custom px-4 py-10 md:py-16 max-w-4xl mx-auto">
          <h1 className="font-display text-2xl md:text-4xl lg:text-5xl text-white font-bold leading-tight" data-testid="result-detail-title">
            {article?.title || `Ha suscitato il maggior interesse stasera ${topHorse?.horse?.name ? `\u2013 ${topHorse.horse.name}` : ''}`}
          </h1>
          <p className="text-white/60 text-sm md:text-base mt-3">{dateLabel}</p>
          <p className="text-white/80 text-sm md:text-base mt-4 leading-relaxed max-w-3xl" style={{ whiteSpace: 'pre-line' }}>
            {article?.content || (
              `L'asta del ${dateLabel} ha visto la partecipazione di ${results.length} cavalli.${topHorse?.horse?.name ? ` ${topHorse.horse.name} ha attirato il maggior interesse` : ''}${topHorse?.current_price > topHorse?.starting_price ? ` raggiungendo un prezzo finale di \u20AC${topHorse.current_price?.toLocaleString()}` : ''}.${results.filter(r => r.winner).length > 0 ? ` ${results.filter(r => r.winner).length} cavalli sono stati venduti.` : ''}`
            )}
          </p>
        </div>
      </section>

      {/* Image - Travera style: straddles navy and white sections, rounded corners */}
      {heroImage && (
        <div className="relative" data-testid="result-detail-hero-image">
          {/* Navy top half background */}
          <div className="absolute top-0 left-0 right-0 h-1/2 bg-[#0a1628]"></div>
          {/* White bottom half */}
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-white"></div>
          {/* Image container */}
          <div className="container-custom px-4 max-w-4xl mx-auto relative z-10">
            <img
              src={heroImage}
              alt={topHorse?.horse?.name || 'Cavallo'}
              className="w-full h-[250px] md:h-[420px] object-cover rounded-2xl shadow-lg"
            />
          </div>
        </div>
      )}

      {/* Article body below image - like Travera */}
      <div className="container-custom px-4 max-w-4xl mx-auto">
        {article?.body && (
          <div className="pt-8 md:pt-10 pb-4" data-testid="result-detail-body">
            {renderBody(article.body)}
          </div>
        )}

        {/* Results table - clean Travera style */}
        <div className="overflow-x-auto pt-4 md:pt-6" data-testid="result-detail-table-container">
          <table className="w-full" data-testid="result-detail-table">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left py-3 pr-4 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Cavallo</th>
                <th className="text-left py-3 pr-4 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Tipo</th>
                <th className="text-left py-3 pr-4 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Acquirente</th>
                <th className="text-right py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide">Prezzo</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => (
                <tr key={result.auction_id} className="border-b border-gray-100">
                  <td className="py-3.5 pr-4 text-sm">
                    <a href={`/asta/${result.auction_id}`} className="text-[#0a1628] hover:text-[#0a7e8c] transition-colors">
                      {result.horse?.name || result.title}
                    </a>
                  </td>
                  <td className="py-3.5 pr-4 text-sm text-[#64748b]">
                    {result.horse?.horse_type ? (
                      <>
                        {typeLabel(result.horse.horse_type)}
                        {result.horse.pregnant_with && ` – incinta di ${result.horse.pregnant_with}`}
                      </>
                    ) : '-'}
                  </td>
                  <td className="py-3.5 pr-4 text-sm">
                    {result.winner ? (
                      <span className="text-[#0a1628]">{result.winner.name}</span>
                    ) : result.current_price > result.starting_price ? (
                      <span className="text-[#0a1628]">Ricompra</span>
                    ) : (
                      <span className="text-[#0a1628]">Non venduto</span>
                    )}
                  </td>
                  <td className="py-3.5 text-right text-sm">
                    {result.winner || result.current_price > result.starting_price ? (
                      <span className="text-[#0a1628]">{'\u20AC'}{result.current_price?.toLocaleString()}</span>
                    ) : (
                      <span className="text-[#64748b]">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Author & date - Travera style with icon */}
        <div className="mt-10 pt-6 border-t border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
            </div>
            <div>
              <p className="text-sm text-[#0a1628]">
                <span className="font-medium">Di:</span> {article?.author || 'Equus Prime'}
              </p>
              <p className="text-sm text-[#64748b]">{dateLabel}</p>
            </div>
          </div>
        </div>

        {/* Condividi - Large buttons like user's reference */}
        <div className="mt-8" data-testid="result-detail-sharing">
          <h3 className="font-display text-xl md:text-2xl text-[#0a1628] mb-4">Condividi</h3>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => handleShare('facebook')}
              className="flex items-center gap-2 px-5 py-3 bg-[#1877f2] text-white font-medium rounded hover:bg-[#1565d8] transition-colors"
              data-testid="share-facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
              Facebook
            </button>
            <button
              onClick={() => handleShare('whatsapp')}
              className="flex items-center gap-2 px-5 py-3 bg-[#25d366] text-white font-medium rounded hover:bg-[#1fb855] transition-colors"
              data-testid="share-whatsapp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </button>
            <button
              onClick={() => handleShare('twitter')}
              className="flex items-center gap-2 px-5 py-3 bg-[#0a0a0a] text-white font-medium rounded hover:bg-[#333] transition-colors"
              data-testid="share-twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X
            </button>
          </div>
        </div>

        {/* "Altro da Equus Prime" - uses attualita feed */}
        {attualita.length > 0 && (
          <div className="mt-14 pt-8 pb-16 border-t border-gray-200 bg-white" data-testid="result-detail-more">
            <h2 className="font-display text-2xl md:text-3xl font-bold text-[#0a1628] text-center mb-8">Altro da Equus Prime</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {attualita.slice(0, 4).map(item => (
                <a
                  key={`${item.type}-${item.id}`}
                  href={item.link}
                  className="group block"
                  data-testid={`more-item-${item.id}`}
                >
                  {item.image_url ? (
                    <div className="overflow-hidden rounded-lg">
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-40 object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                  ) : (
                    <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center">
                      <span className="text-gray-400 text-sm">Nessuna foto</span>
                    </div>
                  )}
                  <h3 className="text-sm font-bold text-[#0a1628] group-hover:text-[#0a7e8c] transition-colors leading-snug mt-3">
                    {item.title}
                  </h3>
                  <p className="text-xs text-[#64748b] mt-1">
                    {item.date ? new Date(item.date).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                  </p>
                </a>
              ))}
            </div>
            <div className="text-center mt-8">
              <a href="/attualita" className="inline-block bg-[#0a8a8a] text-white px-8 py-3 font-medium hover:bg-[#087878] transition-colors rounded" data-testid="all-news-btn">
                Mostra tutte le notizie
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultDetailPage;
